import { START_MONTH, START_YEAR } from '../constants';
import { fmtMoney } from './formatters';
import {
  resolveFacilitySize,
  resolveRequiredMaxExposure,
} from './resolvePlanCovenantLimits';
import { isResultsCalendarAligned, resolvePlanStartMonth } from './planStartMonth';
import { resultMonthLabel } from './monthLabel';

export const REPAYMENT_GRACE_PERIOD_LABEL = 'Repayment grace period (months)';

export function resolveExportMonthLabelsFromState(state) {
  const marketingMonths = state?.marketing?.length ?? 0;
  if (marketingMonths <= 0) {
    return [];
  }
  const planStartYear = state.planStartYear ?? START_YEAR;
  const planStartMonth = resolvePlanStartMonth(state);
  const calendarAlignedCols = isResultsCalendarAligned(state);
  const monthCount = marketingMonths + 1;
  return Array.from({ length: monthCount }, (_, index) =>
    resultMonthLabel(index, planStartYear, planStartMonth, calendarAlignedCols)
  );
}

export function buildExecutiveModelContextRows(state, calcTimestamp, { companyName } = {}) {
  return [
    ...(companyName?.trim() ? [['Customer', companyName.trim()]] : []),
    ['Model name', state.modelName?.trim() || '—'],
    [
      'Calculated',
      calcTimestamp ? calcTimestamp.toLocaleString() : '—',
    ],
    ['GPM %', state.gpm ?? 0],
    ['Opex (monthly)', state.opex ?? 0],
    ['Beginning cash', state.beginCash ?? 0],
    ['Revenue growth YoY %', state.revenueGrowthYoy ?? 0],
  ];
}

export function buildModelContextRows(state, calcTimestamp, { companyName } = {}) {
  return [
    ...(companyName?.trim() ? [['Customer', companyName.trim()]] : []),
    ['Model name', state.modelName?.trim() || '—'],
    [
      'Calculated',
      calcTimestamp ? calcTimestamp.toLocaleString() : '—',
    ],
    ['Facility size', resolveFacilitySize(state)],
    ['Facility fee %', state.facilityFeePct ?? 0],
    ['Facility fee installments', state.facilityFeeInstallments ?? 1],
    [REPAYMENT_GRACE_PERIOD_LABEL, state.gracePeriod ?? 0],
    ['GPM %', state.gpm ?? 0],
    ['Opex (monthly)', state.opex ?? 0],
    ['Beginning cash', state.beginCash ?? 0],
    ['Fee type', state.feeType ?? '—'],
    ['Fee / target XIRR %', state.feePct ?? 0],
  ];
}

export function buildResultsKpis(calcResult, state) {
  const {
    totalIRR,
    totalWithdrawal,
    totalRepayments,
    runoffAtLastWithdrawal,
    endingCashAtLastWithdrawal,
    totalRevenue,
    peakTwelveBalance,
    lastDrawMonth,
    cols,
  } = calcResult;

  const lastDrawLabel =
    lastDrawMonth >= 0 ? cols[lastDrawMonth] : '—';
  const actualFee = totalRepayments - totalWithdrawal;
  const cumRoas = (state.roas[state.roas.length - 1] || 0).toFixed(0);

  return [
    { label: 'Total IRR', value: totalIRR, format: 'irr', footer: 'annualized' },
    {
      label: 'Total withdrawal',
      value: totalWithdrawal,
      format: 'money',
      footer: 'sum of Twelve fundings',
    },
    {
      label: 'Total repayments',
      value: totalRepayments,
      format: 'money',
      footer: 'sum of cohort repayments',
    },
    {
      label: 'Actual fee',
      value: actualFee,
      format: 'money',
      footer: 'repayments − withdrawals',
    },
    {
      label: 'Runoff at last withdrawal',
      value: runoffAtLastWithdrawal,
      format: 'money',
      footer: `revenue tail after ${lastDrawLabel}`,
    },
    {
      label: 'Ending cash at last withdrawal',
      value: endingCashAtLastWithdrawal,
      format: 'money',
      footer: `at end of ${lastDrawLabel}`,
    },
    {
      label: 'Total revenue',
      value: totalRevenue,
      format: 'money',
      footer: `Cum ROAS ${cumRoas}%`,
    },
    {
      label: 'Max exposure',
      value: peakTwelveBalance,
      format: 'money',
      footer: `vs ${fmtMoney(resolveRequiredMaxExposure(state.maxExposure))} max exposure`,
    },
  ];
}

/**
 * @returns {Array<{ type: 'section'|'data'|'spacer'|'perspective', label: string, values?: number[], bold?: boolean, indent?: boolean, totalValue?: number }>}
 */
export function buildResultsTableRows(calcResult, { includeIncomeDetail = true } = {}) {
  const rows = [];
  const data = (label, arr, opts = {}) =>
    rows.push({ type: 'data', label, values: arr, ...opts });
  const section = (label) => rows.push({ type: 'section', label });

  section('Income statement');
  data('Revenue', calcResult.revenue, { bold: true });
  if (calcResult.revenueNewCohorts && includeIncomeDetail) {
    data('Revenue — new cohorts', calcResult.revenueNewCohorts, {
      indent: true,
    });
  }
  if (calcResult.revenueOldCohorts && includeIncomeDetail) {
    data('Revenue — old cohorts', calcResult.revenueOldCohorts, {
      indent: true,
    });
  }
  if (includeIncomeDetail) {
    data('COGS', calcResult.cogs, { indent: true });
    data('Gross profit', calcResult.grossProfit, { bold: true, indent: true });
    data('Marketing spend', calcResult.directMarketing, { indent: true });
    data('Contribution', calcResult.contribution, { bold: true, indent: true });
    data('Opex', calcResult.opexRow, { indent: true });
  }
  data('EBITDA', calcResult.ebitda, { bold: true });

  section('Twelve balance');
  data('Beginning balance', calcResult.beginBal);
  data('Draw', calcResult.draw, { indent: true });
  data('Fee', calcResult.xirrFee, { indent: true });
  data('Facility fee', calcResult.facFeeRow, { indent: true });
  data('Repayment', calcResult.repay, { indent: true });
  data('Ending balance', calcResult.endBal, { bold: true });

  section('Cash');
  data('Beginning cash', calcResult.beginCashRow, {
    totalValue: calcResult.beginCashTotal,
  });
  data('EBITDA', calcResult.ebitda);
  data('Twelve Finance', calcResult.twelveFinance, { bold: true });
  data('Ending cash balance', calcResult.cashEnd, { bold: true });

  rows.push({ type: 'spacer', label: '' });
  rows.push({
    type: 'perspective',
    label: 'Twelve perspective — IRR basis',
  });
  data('Twelve net cashflow', calcResult.twelveCF, { bold: true });
  data('Exposure to Twelve', calcResult.endBal, {
    indent: true,
    skipTotal: true,
  });
  data('Runrate', calcResult.runrate, { indent: true });
  data('Runrate exposure', calcResult.runrateExposurePct, {
    indent: true,
    format: 'exposurePct',
    skipTotal: true,
  });
  data('Runoff', calcResult.runoff, { indent: true });
  data('Runoff exposure', calcResult.runoffExposurePct, {
    indent: true,
    format: 'exposurePct',
    skipTotal: true,
  });
  if (calcResult.runoffExposureCohortCount != null) {
    data(
      `Runoff (${calcResult.runoffExposureCohortCount} cohorts)`,
      calcResult.runoffCohortWindow,
      { indent: true, skipTotal: true }
    );
    data(
      `Runoff exposure (${calcResult.runoffExposureCohortCount} cohorts)`,
      calcResult.runoffCohortWindowExposurePct,
      { indent: true, format: 'exposurePct', skipTotal: true }
    );
  }

  return rows;
}

/**
 * Standalone cashflow rows shown on the View tab (no Twelve facility / IRR sections).
 */
export function buildExecutiveResultsTableRows(
  calcResult,
  { includeRevenueBreakdown = false } = {}
) {
  const rows = [];
  const data = (label, arr, opts = {}) =>
    rows.push({ type: 'data', label, values: arr, ...opts });
  const section = (label) => rows.push({ type: 'section', label });

  section('Income statement');
  data('Revenue', calcResult.revenue, { bold: true });
  if (includeRevenueBreakdown && calcResult.revenueNewCohorts) {
    data('Revenue — new cohorts', calcResult.revenueNewCohorts, {
      indent: true,
    });
  }
  if (includeRevenueBreakdown && calcResult.revenueOldCohorts) {
    data('Revenue — old cohorts', calcResult.revenueOldCohorts, {
      indent: true,
    });
  }
  data('COGS', calcResult.cogs, { indent: true });
  data('Gross profit', calcResult.grossProfit, { bold: true, indent: true });
  data('Marketing spend', calcResult.directMarketing, { indent: true });
  data('Contribution', calcResult.contribution, { bold: true, indent: true });
  data('Opex', calcResult.opexRow, { indent: true });
  data('EBITDA', calcResult.ebitda, { bold: true });

  section('Cash');
  data('Beginning cash', calcResult.beginCashRow, {
    totalValue: calcResult.beginCashTotal,
  });
  data('EBITDA', calcResult.ebitda, { rowKey: 'cash-ebitda' });
  data('Ending cash balance', calcResult.cashEnd, { bold: true });

  return rows;
}

export function buildCohortRepaymentsMatrix(calcResult) {
  const { cols, cohortRepayments, cohortDraws } = calcResult;
  const header = [
    'cohort',
    'marketing_month',
    'draw_usd',
    ...cols,
    'total',
  ];

  const bodyRows = cohortRepayments.map((amounts, i) => {
    const total = amounts.reduce((sum, v) => sum + v, 0);
    return [
      `M${i}`,
      cols[i] || '',
      cohortDraws[i] || 0,
      ...amounts,
      total,
    ];
  });

  const monthTotals = cols.map((_, t) =>
    cohortRepayments.reduce((sum, row) => sum + (row[t] || 0), 0)
  );
  const grandTotal = monthTotals.reduce((sum, v) => sum + v, 0);
  bodyRows.push([
    'Total',
    '',
    cohortDraws.reduce((sum, v) => sum + v, 0),
    ...monthTotals,
    grandTotal,
  ]);

  return { header, rows: bodyRows, moneyColumnStart: 3 };
}

export function buildEmptyCohortRepaymentsMatrix(monthLabels = []) {
  return {
    header: ['cohort', 'marketing_month', 'draw_usd', ...monthLabels, 'total'],
    rows: [],
    moneyColumnStart: 3,
  };
}

export function buildEmptyCohortRevenueSections(monthLabels = []) {
  const header = ['cohort', 'marketing_month', 'spend_usd', ...monthLabels, 'total'];
  return {
    sections: [
      {
        title: 'New cohorts',
        header,
        rows: [],
      },
    ],
    moneyColumnStart: 2,
  };
}

function buildRevenueTableRows(cols, cohortRows, rowMetaList) {
  const bodyRows = cohortRows.map((amounts, rowIndex) => {
    const { cohortLabel, marketingMonth, marketingSpend } =
      rowMetaList[rowIndex];
    const total = amounts.reduce((sum, value) => sum + value, 0);
    return [
      cohortLabel,
      marketingMonth,
      marketingSpend,
      ...amounts,
      total,
    ];
  });

  const monthTotals = cols.map((_, monthIndex) =>
    cohortRows.reduce((sum, row) => sum + (row[monthIndex] || 0), 0)
  );
  const marketingTotal = rowMetaList.reduce(
    (sum, meta) => sum + (meta.marketingSpend || 0),
    0
  );
  const grandTotal = monthTotals.reduce((sum, value) => sum + value, 0);
  bodyRows.push([
    'Total',
    '',
    marketingTotal,
    ...monthTotals,
    grandTotal,
  ]);

  return bodyRows;
}

export function buildCohortRevenueSections(calcResult) {
  const { cols, cohortRevenue, cohortMarketing, oldCohortRevenueRows = [] } =
    calcResult;
  const header = [
    'cohort',
    'marketing_month',
    'marketing_usd',
    ...cols,
    'total',
  ];

  const newMeta = cohortRevenue.map((_, rowIndex) => ({
    cohortLabel: `M${rowIndex}`,
    marketingMonth: cols[rowIndex] || '',
    marketingSpend: cohortMarketing[rowIndex] || 0,
  }));
  const sections = [
    {
      title: null,
      header,
      rows: buildRevenueTableRows(cols, cohortRevenue, newMeta),
    },
  ];

  if (oldCohortRevenueRows.length > 0) {
    const oldMeta = oldCohortRevenueRows.map((row) => ({
      cohortLabel: row.cohortLabel,
      marketingMonth: row.marketingMonth,
      marketingSpend: row.marketingSpend,
    }));
    const oldAmounts = oldCohortRevenueRows.map((row) => row.amounts);
    sections.push({
      title: 'Old cohorts',
      header,
      rows: buildRevenueTableRows(cols, oldAmounts, oldMeta),
    });
  }

  return { sections, moneyColumnStart: 2 };
}

export function getResultsTableColumnLabels(calcResult) {
  return {
    monthLabels: calcResult.cols,
  };
}
